/**
 * Pure Swiss invoice arithmetic (spec: faktura.md, Story 4, non-functional
 * "Genauigkeit"). All money math happens in integer Rappen (cents) to avoid
 * floating-point drift — every function converts to cents on the way in and
 * back to CHF on the way out.
 */

/** Converts a CHF amount to integer Rappen, rounding to the nearest cent. */
function toCents(amount: number): number {
  return Math.round(amount * 100);
}

function fromCents(cents: number): number {
  return cents / 100;
}

/** Rounds an hours × rate line amount to the nearest Rappen — the authoritative server-side computation (never trust a client-supplied amount). */
export function computeLineAmount(quantityHours: number, rate: number): number {
  return fromCents(toCents(quantityHours * rate));
}

/** Converts booked minutes to hours with two decimal places (minutes are always multiples of 15, so this is always exact). */
export function minutesToHours(minutes: number): number {
  return fromCents(Math.round((minutes / 60) * 100));
}

export interface InvoiceLineAmountInput {
  amount: number;
  vatRate: number;
}

export interface InvoiceTotals {
  /** Sum of all line amounts. */
  netAmount: number;
  /** Sum of VAT computed per rate group (see below), rounded up to the next Rappen. */
  vatAmount: number;
  /** `grossAmount - (netAmount + vatAmount)` — the 5-Rappen adjustment, can be negative. */
  roundingAmount: number;
  /** `netAmount + vatAmount`, rounded to the nearest 5 Rappen (Swiss cash rounding). */
  grossAmount: number;
}

/**
 * Computes the invoice totals from its (already rate-computed) lines:
 * - net = sum of all line amounts,
 * - VAT is computed PER RATE GROUP (not per line) and rounded UP to the next
 *   Rappen — grouping first avoids losing a fiscal Rappen to repeated
 *   per-line rounding,
 * - the gross total is rounded to the nearest 5 Rappen (Swiss cash rounding);
 *   the difference to the unrounded gross is reported as `roundingAmount`.
 */
export function computeInvoiceTotals(lines: readonly InvoiceLineAmountInput[]): InvoiceTotals {
  const netCents = lines.reduce((sum, line) => sum + toCents(line.amount), 0);

  const netCentsByVatRate = new Map<number, number>();
  for (const line of lines) {
    netCentsByVatRate.set(line.vatRate, (netCentsByVatRate.get(line.vatRate) ?? 0) + toCents(line.amount));
  }

  let vatCents = 0;
  for (const [vatRate, groupNetCents] of netCentsByVatRate) {
    vatCents += Math.ceil((groupNetCents * vatRate) / 100);
  }

  const grossUnroundedCents = netCents + vatCents;
  const grossRoundedCents = Math.round(grossUnroundedCents / 5) * 5;

  return {
    netAmount: fromCents(netCents),
    vatAmount: fromCents(vatCents),
    roundingAmount: fromCents(grossRoundedCents - grossUnroundedCents),
    grossAmount: fromCents(grossRoundedCents),
  };
}

export interface VatGroupBreakdown {
  vatRate: number;
  /** Sum of line amounts at this VAT rate. */
  netAmount: number;
  /** VAT of this group, rounded up to the next Rappen (same rule as `computeInvoiceTotals`). */
  vatAmount: number;
}

/** Per-rate-group breakdown (net + VAT) — the live-preview table needs this in addition to the invoice-wide totals. */
export function computeVatBreakdown(lines: readonly InvoiceLineAmountInput[]): VatGroupBreakdown[] {
  const netCentsByVatRate = new Map<number, number>();
  for (const line of lines) {
    netCentsByVatRate.set(line.vatRate, (netCentsByVatRate.get(line.vatRate) ?? 0) + toCents(line.amount));
  }
  return [...netCentsByVatRate.entries()]
    .map(([vatRate, netCents]) => ({
      vatRate,
      netAmount: fromCents(netCents),
      vatAmount: fromCents(Math.ceil((netCents * vatRate) / 100)),
    }))
    .sort((a, b) => b.vatRate - a.vatRate);
}
